import React from 'react';
import { Paper, Box, Button, TextField, Typography, Grid, Accordion, AccordionSummary, AccordionDetails, Divider } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Form, Field } from 'react-final-form';
import ImageUploadField from '../ImageUploadField';

const ColorField = ({ name, label, defaultValue }) => (
  <Field name={name}>
    {({ input }) => (
      <Box mt={2}>
        <TextField
          label={label}
          value={input.value}
          onChange={input.onChange}
          placeholder={defaultValue}
          helperText="HEX color code"
          fullWidth
        />
        <Box mt={1} display="flex" alignItems="center" gap={1}>
          <Box
            width={30}
            height={30}
            bgcolor={input.value || defaultValue}
            borderRadius={4}
            border="1px solid #ccc"
          />
          <input
            type="color"
            value={input.value || defaultValue}
            onChange={(e) => input.onChange(e.target.value)}
            style={{ cursor: 'pointer' }}
          />
        </Box>
      </Box>
    )}
  </Field>
);

const LevelsTab = ({ config, onSave, levelCount }) => {
  const handleSubmit = (values) => {
    const updatedLevels = [];
    for (let i = 1; i <= levelCount; i += 1) {
      const existingLevel = config?.levels?.find((l) => l.level_number === i) || {};
      updatedLevels.push({
        ...existingLevel,
        level_number: i,
        level_image: values[`level_${i}_image`],
        level_name: values[`level_${i}_name`],
        level_name_color: values[`level_${i}_name_color`],
        game_background: values[`level_${i}_game_background`],
        highlight_color: values[`level_${i}_highlight_color`],
        result_success_background: values[`level_${i}_result_success_background`],
        result_unsuccess_background: values[`level_${i}_result_unsuccess_background`],
        result_success_cta_color: values[`level_${i}_result_success_cta_color`],
        result_exit_border_color: values[`level_${i}_result_exit_border_color`],
        result_success_text_color: values[`level_${i}_result_success_text_color`],
        result_unsuccessful_text_color: values[`level_${i}_result_unsuccessful_text_color`],
        result_credits_color: values[`level_${i}_result_credits_color`],
      });
    }

    onSave({
      ...config,
      levels: updatedLevels,
    });
  };

  const getInitialValues = () => {
    const values = {};
    for (let i = 1; i <= levelCount; i += 1) {
      const level = config?.levels?.find((l) => l.level_number === i) || {};
      values[`level_${i}_image`] = level.level_image;
      values[`level_${i}_name`] = level.level_name;
      values[`level_${i}_name_color`] = level.level_name_color;
      values[`level_${i}_game_background`] = level.game_background;
      values[`level_${i}_highlight_color`] = level.highlight_color;
      values[`level_${i}_result_success_background`] = level.result_success_background;
      values[`level_${i}_result_unsuccess_background`] = level.result_unsuccess_background;
      values[`level_${i}_result_success_cta_color`] = level.result_success_cta_color;
      values[`level_${i}_result_exit_border_color`] = level.result_exit_border_color;
      values[`level_${i}_result_success_text_color`] = level.result_success_text_color;
      values[`level_${i}_result_unsuccessful_text_color`] = level.result_unsuccessful_text_color;
      values[`level_${i}_result_credits_color`] = level.result_credits_color;
    }
    return values;
  };

  return (
    <Paper>
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Level Settings (8 Levels)
        </Typography>
        <Form
          onSubmit={handleSubmit}
          initialValues={getInitialValues()}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              {Array.from({ length: levelCount }, (_, i) => i + 1).map((levelNum) => (
                <Accordion key={levelNum} defaultExpanded={levelNum === 1}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">Level {levelNum}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Level Selector Appearance
                        </Typography>

                        <ImageUploadField
                          name={`level_${levelNum}_image`}
                          label="Level Image"
                          folder="word-finder/levels/"
                          helperText="Minimum 375Wx825H"
                        />

                        <Field name={`level_${levelNum}_name`}>
                          {({ input }) => (
                            <Box mt={2}>
                              <TextField label="Level Name" value={input.value} onChange={input.onChange} fullWidth />
                            </Box>
                          )}
                        </Field>

                        <Field name={`level_${levelNum}_name_color`}>
                          {({ input }) => (
                            <Box mt={2}>
                              <TextField
                                label="Level Name Color"
                                value={input.value}
                                onChange={input.onChange}
                                placeholder="#ffffff"
                                fullWidth
                              />
                              <Box mt={1} display="flex" alignItems="center" gap={1}>
                                <Box
                                  width={30}
                                  height={30}
                                  bgcolor={input.value || '#ffffff'}
                                  borderRadius={4}
                                  border="1px solid #ccc"
                                />
                                <input
                                  type="color"
                                  value={input.value || '#ffffff'}
                                  onChange={(e) => input.onChange(e.target.value)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </Box>
                            </Box>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Game Screen
                        </Typography>

                        <ImageUploadField
                          name={`level_${levelNum}_game_background`}
                          label="Game Background"
                          folder="word-finder/levels/"
                          helperText="Minimum 375Wx825H"
                        />

                        <Field name={`level_${levelNum}_highlight_color`}>
                          {({ input }) => (
                            <Box mt={2}>
                              <TextField
                                label="Selected Letters Highlight Color"
                                value={input.value}
                                onChange={input.onChange}
                                placeholder="#db0a40"
                                fullWidth
                              />
                              <Box mt={1} display="flex" alignItems="center" gap={1}>
                                <Box
                                  width={30}
                                  height={30}
                                  bgcolor={input.value || '#db0a40'}
                                  borderRadius={4}
                                  border="1px solid #ccc"
                                />
                                <input
                                  type="color"
                                  value={input.value || '#db0a40'}
                                  onChange={(e) => input.onChange(e.target.value)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </Box>
                            </Box>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={12}>
                        <Box mt={3} mb={2}>
                          <Divider />
                        </Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Result Screen Settings
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Background image for successful and unsuccessful game result screens
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Success Screen
                        </Typography>

                        <ImageUploadField
                          name={`level_${levelNum}_result_success_background`}
                          label="Success Background"
                          folder="word-finder/levels/"
                          helperText="Minimum 375Wx825H"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Unsuccess Screen
                        </Typography>

                        <ImageUploadField
                          name={`level_${levelNum}_result_unsuccess_background`}
                          label="Unsuccess Background"
                          folder="word-finder/levels/"
                          helperText="Minimum 375Wx825H"
                        />
                      </Grid>

                      <Grid item xs={12} md={12}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Colors for successful and unsuccessful game result screens
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Button Colors
                        </Typography>

                        <ColorField
                          name={`level_${levelNum}_result_success_cta_color`}
                          label="Ujra/Tovabb CTA Button Color"
                          defaultValue="#db0a40"
                        />

                        <ColorField
                          name={`level_${levelNum}_result_exit_border_color`}
                          label="Kilepes CTA Border Color"
                          defaultValue="#db0a40"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Text Colors
                        </Typography>

                        <ColorField
                          name={`level_${levelNum}_result_success_text_color`}
                          label="Sikeres Text Color (+ Credit Amount)"
                          defaultValue="#00ff00"
                        />

                        <ColorField
                          name={`level_${levelNum}_result_unsuccessful_text_color`}
                          label="Sikertelen Text Color"
                          defaultValue="#ff0000"
                        />

                        <ColorField
                          name={`level_${levelNum}_result_credits_color`}
                          label="Credits Amount Color"
                          defaultValue="#ffd700"
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Box mt={3}>
                <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                  Save All Level Settings
                </Button>
              </Box>
            </form>
          )}
        />
      </Box>
    </Paper>
  );
};

export default LevelsTab;
